import React, { useState } from 'react';
import {
  Button,
  Drawer,
  Typography,
  Divider,
  TextField,
  Select,
  MenuItem,
  IconButton,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
// Styles
import { Close } from '@mui/icons-material';
import { Field, Form, Formik, ErrorMessage, FieldProps } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import { fetchUserList, updateUserAction } from './actions';
import { LoadingButton } from '@mui/lab';
import { Watch } from 'react-loader-spinner';
import * as Yup from 'yup';
import { setErrorToastState } from 'Store/action';
import { set_edit_user_drawer } from 'Store/reducers/adminReducer';
import { set_users, set_user_details } from 'Store/reducers/userReducer';
import { AppDispatch, RootState } from 'Store/Store';
import { EMAIL_REGEX } from 'shared/constants/regex';

export const EditUser: React.FC = (): React.ReactElement => {
  const dispatch = useDispatch<AppDispatch>();
  const edit_user_drawer = useSelector((state: RootState) => state?.admin?.edit_user_drawer);
  const user_details = useSelector((state: RootState) => state?.user?.user_details);
  const [loading, setLoading] = useState(false);
  const [serverErrorMessage, setServerErrorMessage] = useState('');

  const updateUser = (data: any) => {
    setServerErrorMessage('');
    setLoading(true);

    if (user_details) {
      updateUserAction(user_details?.id, data)
        .then(res => {
          if (res.status === 200) {
            fetchUserList().then(res => {
              if (res.status === 200) {
                dispatch(set_users(res.data));
              }
            });
            dispatch(set_edit_user_drawer(false));
            dispatch(set_user_details(res.data));
          }
          setLoading(false);
        })
        .catch(err => {
          setLoading(false);
          dispatch(setErrorToastState(true, 'Something went wrong, please try again in a while.'));
        });
    }
  };

  return (
    <Drawer
      BackdropProps={{ invisible: true }}
      anchor="right"
      open={edit_user_drawer}
      sx={{ zIndex: theme => theme.zIndex.drawer + 2 }}>
      <Formik
        validationSchema={Yup.object().shape({
          first_name: Yup.string().max(255).required('Required!'),
          last_name: Yup.string().max(255).required('Required!'),
          email: Yup.string().max(255).email('Invalid email').matches(EMAIL_REGEX, 'Invalid email format').required('Required!'),    
          phone: Yup.string().max(255),
          password: Yup.string().matches(
            /^(?=.*[A-Za-z])(?=.*\d)(?=.*[@$!%*#?&])[A-Za-z\d@$!%*#?&]{8,}$/,
            'Password must contain at least 8 characters, one uppercase, one number and one special case character',
          ),
          role: Yup.string().min(1).required('Required!'),
        })}
        onSubmit={updateUser}
        enableReinitialize
        initialValues={{
          first_name: user_details?.first_name || '',
          last_name: user_details?.last_name || '',
          email: user_details?.email || '',
          phone: user_details?.phone || '',
          password: user_details?.password || '',
          role: user_details?.role?.name || '',
        }}>
        {({ touched, errors, handleSubmit }) => (
          <Form style={{ height: '100%' }}>
            <Grid
              container
              direction="column"
              flexWrap="nowrap"
              justifyContent="space-between"
              spacing={4}>
              <Grid container direction="column" spacing={4}>
                <Grid container direction="row" justifyContent="space-between" alignItems="center">
                  <Grid>
                    <Typography variant="h4">Update User</Typography>
                  </Grid>
                  <Grid>
                    <IconButton
                      disabled={loading}
                      onClick={() => dispatch(set_edit_user_drawer(false))}>
                      <Close />
                    </IconButton>
                  </Grid>
                </Grid>
                <Grid>
                  <Typography variant="h6">User account</Typography>
                </Grid>
                <Grid container direction="column" spacing={1}>
                  <Grid container spacing={2}>
                    <Grid
                      size={{
                        xs: 12,
                        md: 6,
                      }}
                      container
                      spacing={1}
                      direction="column">
                      <Grid>
                        <Typography variant="h5">First name</Typography>
                      </Grid>
                      <Grid>
                        <Field name="first_name">
                          {({ field }: FieldProps) => (
                            <TextField
                              fullWidth
                              {...field}
                              variant="standard"
                              InputProps={{
                                disableUnderline: true,
                              }}
                            />
                          )}
                        </Field>
                      </Grid>
                      <Grid>
                        <ErrorMessage name="first_name">
                          {message => <Typography color="error">{message}</Typography>}
                        </ErrorMessage>
                      </Grid>
                    </Grid>
                    <Grid
                      size={{
                        xs: 12,
                        md: 6,
                      }}
                      container
                      spacing={1}
                      direction="column">
                      <Grid>
                        <Typography variant="h5">Last name</Typography>
                      </Grid>
                      <Grid>
                        <Field name="last_name">
                          {({ field }: FieldProps) => (
                            <TextField
                              fullWidth
                              {...field}
                              variant="standard"
                              InputProps={{
                                disableUnderline: true,
                              }}
                            />
                          )}
                        </Field>
                      </Grid>
                      <Grid>
                        <ErrorMessage name="last_name">
                          {message => <Typography color="error">{message}</Typography>}
                        </ErrorMessage>
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid container direction="column" spacing={1}>
                    <Grid>
                      <Typography variant="h5">Email</Typography>
                    </Grid>
                    <Grid>
                      <Field name="email">
                        {({ field }: FieldProps) => (
                          <TextField
                            fullWidth
                            {...field}
                            variant="standard"
                            InputProps={{
                              disableUnderline: true,
                            }}
                          />
                        )}
                      </Field>
                    </Grid>
                    <Grid>
                      <ErrorMessage name="email">
                        {message => <Typography color="error">{message}</Typography>}
                      </ErrorMessage>
                    </Grid>
                  </Grid>
                  <Grid container direction="column" spacing={1}>
                    <Grid>
                      <Typography variant="h5">Phone Number</Typography>
                    </Grid>
                    <Grid>
                      <Field name="phone">
                        {({ field }: FieldProps) => (
                          <TextField
                            fullWidth
                            placeholder="+44 (0) 7123 456 789"
                            {...field}
                            variant="standard"
                            InputProps={{
                              disableUnderline: true,
                            }}
                          />
                        )}
                      </Field>
                    </Grid>
                    <Grid>
                      <ErrorMessage name="phone">
                        {message => <Typography color="error">{message}</Typography>}
                      </ErrorMessage>
                    </Grid>
                  </Grid>
                  <Grid container direction="column" spacing={1}>
                    <Grid>
                      <Typography variant="h5">Password</Typography>
                    </Grid>
                    <Grid>
                      <Field name="password">
                        {({ field }: FieldProps) => (
                          <TextField
                            fullWidth
                            {...field}
                            type="password"
                            variant="standard"
                            InputProps={{
                              disableUnderline: true,
                            }}
                          />
                        )}
                      </Field>
                    </Grid>
                    <Grid>
                      <ErrorMessage name="password">
                        {message => <Typography color="error">{message}</Typography>}
                      </ErrorMessage>
                    </Grid>
                  </Grid>
                </Grid>
                <Grid>
                  <Divider color="#F1F1F1" />
                </Grid>
                <Grid>
                  <Typography variant="h6">User account</Typography>
                </Grid>
                <Grid container direction="column" spacing={1}>
                  <Grid>
                    <Typography variant="h5">Role</Typography>
                  </Grid>
                  <Grid>
                    <Field name="role">
                      {({ field }: FieldProps) => (
                        <Select
                          disableUnderline
                          {...field}
                          fullWidth
                          value={field.value}
                          variant="standard"
                          error={touched.role && !!errors.role ? true : false}>
                          <MenuItem value="" selected disabled>
                            Select Role
                          </MenuItem>
                          <MenuItem value="super_admin">Super Admin</MenuItem>
                          <MenuItem value="client">Client</MenuItem>
                        </Select>
                      )}
                    </Field>
                  </Grid>
                  <Grid>
                    <ErrorMessage name="role">
                      {message => <Typography color="error">{message}</Typography>}
                    </ErrorMessage>
                  </Grid>
                </Grid>
              </Grid>
              <Grid container direction="column" spacing={2} mb={4}>
                {serverErrorMessage && (
                  <Grid>
                    <Typography color="error" textAlign="center">
                      {serverErrorMessage}
                    </Typography>
                  </Grid>
                )}
                <Grid>
                  <LoadingButton
                    onClick={() => handleSubmit()}
                    loadingIndicator={<Watch color="#F1F1F1" width={30} height={20} />}
                    loading={loading}
                    variant="contained"
                    color="secondary"
                    fullWidth>
                    Update User
                  </LoadingButton>
                </Grid>
                <Grid>
                  <Button
                    disabled={loading}
                    variant="outlinedWhite"
                    onClick={() => dispatch(set_edit_user_drawer(false))}
                    fullWidth>
                    Cancel
                  </Button>
                </Grid>
              </Grid>
            </Grid>
          </Form>
        )}
      </Formik>
    </Drawer>
  );
};

export default EditUser;
